import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import StatusBadge from '@/components/common/StatusBadge';
import { getBuildingById, getFloorById, getRoomById, getBedSpacesByRoom } from '@/services/nocodeBackendService';

const { FiUser, FiArrowLeft, FiTag } = FiIcons;

const RoomDetails = () => {
  const { buildingId, floorId, roomId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ building: null, floor: null, room: null, bedSpaces: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getBuildingById(buildingId),
      getFloorById(floorId),
      getRoomById(roomId),
      getBedSpacesByRoom(roomId)
    ])
    .then(([bData, fData, rData, bedData]) => {
      setData({ building: bData, floor: fData, room: rData, bedSpaces: bedData });
    })
    .catch(() => navigate(`/accommodation/${buildingId}/floor/${floorId}`))
    .finally(() => setLoading(false));
  }, [buildingId, floorId, roomId, navigate]);

  if (loading) return <div className="p-12 text-center text-slate-500">Loading room details...</div>;
  if (!data.room) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[
        { label: 'Accommodation', path: '/accommodation' },
        { label: data.building.name, path: `/accommodation/${buildingId}` },
        { label: data.floor.floor_name, path: `/accommodation/${buildingId}/floor/${floorId}` },
        { label: data.room.room_name }
      ]} />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600 transition-colors">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          {data.room.room_name} Bed Spaces
        </h1>
        <p className="text-slate-600 mt-2 ml-10">Individual bed spaces for secure monthly rental.</p>
        <div className="ml-10 mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg inline-block text-sm text-blue-800">
          <strong>Note:</strong> We only rent individual bed spaces. Whole-room reservations are not permitted.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.bedSpaces.map(bed => (
          <div key={bed.id} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-sky-100 p-3 rounded-full text-sky-600">
                  <SafeIcon icon={FiUser} className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">{bed.bed_space_name}</h3>
                  {bed.is_demo && <span className="text-xs text-amber-600 font-medium">Demo Data</span>}
                </div>
              </div>
              <StatusBadge status={bed.status} />
            </div>
            
            <div className="border-t border-slate-100 pt-4 mt-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 flex items-center gap-2">
                  <SafeIcon icon={FiTag} className="w-4 h-4" /> Monthly Rate
                </span>
                <span className="font-semibold text-slate-900">₱{bed.monthly_rate}</span>
              </div>
            </div>

            {bed.status === 'available' ? (
              <button className="w-full mt-6 bg-blue-600 text-white font-medium py-2.5 rounded-lg hover:bg-blue-700 transition-colors">
                Inquire Space
              </button>
            ) : (
              <button disabled className="w-full mt-6 bg-slate-100 text-slate-400 font-medium py-2.5 rounded-lg cursor-not-allowed">
                Not Available
              </button>
            )}
          </div>
        ))}
      </div>
      
      {data.bedSpaces.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200 text-slate-500">
          No bed spaces have been configured for this room yet.
        </div>
      )}
    </div>
  );
};

export default RoomDetails;